/*
  File: AgentNewModal.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:
  This file contains the AgentAdditionModal component, which is used for adding a new agent. It includes a multi-step form for collecting agent details and handles the creation of the agent.

  Components:
  - AgentAdditionModal: Main component for adding a new agent.

  Props:
  - addAgentModelVisible: Flag indicating whether the modal is visible.
  - currentStep: Current step in the multi-step form.
  - formData: Data collected from the form.
  - steps: Array of objects representing the steps in the form.
  - handlePrev: Function to handle moving to the previous step.
  - handleNext: Function to handle moving to the next step.
  - handleCancel: Function to handle modal cancellation.
  - setCurrentStep: Function to set the current step.
  - isFormDataValid: Function to check if the form data is valid.
  - createAgent: Function to create the agent.

  Dependencies:
  - antd: Provides components like Modal, Steps, Button, and message for UI elements.

*/

import React, { useState } from "react";
import { Modal, Steps, Button, message } from "antd";
import Header from "./Header"; // Import your Header component
import agentsIcon from "./icons/agents.gif";

interface AddAgentModalProps {
  addAgentModelVisible: boolean;
  currentStep: number;
  formData: any; // Adjust the type as per your formData structure
  steps: { title: string; content: React.ReactNode }[];
  onHandlePrevStep: () => void;
  onHandleNextStep: () => void;
  onHandleCancel: () => void;
  setCurrentStep: (step: number) => void;
  isFormDataValid: (formData: any) => boolean;
  onCreateAgent: () => Promise<void>;
}

const AgentAdditionMoal: React.FC<AddAgentModalProps> = ({
  addAgentModelVisible,
  onHandleCancel: handleCancel,
  currentStep,
  onHandlePrevStep: handlePrev,
  onHandleNextStep: handleNext,
  formData,
  isFormDataValid,
  steps,
  setCurrentStep,
  onCreateAgent: createAgent,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDone = async () => {
    setIsLoading(true);
    try {
      await createAgent();
      message.success("Agent created successfully!");
      handleCancel();
    } catch (error) {
      message.error("Failed to create agent. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={addAgentModelVisible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose={true}
      closable={false}
      className="no-padding-modal"
    >
      <Header
        header="Add Agent"
        subheader="Provide the necessary details to add a new agent, specify agent basic information, and select a referring agent."
        icon={agentsIcon}
      />
      <div style={{ margin: "20px" }}>
        <Steps
          current={currentStep}
          size="small"
          responsive={true}
          onChange={(step) => setCurrentStep(step)}
        >
          {steps.map((step, index) => (
            <Steps.Step key={index} title={step.title} />
          ))}
        </Steps>
      </div>
      <div style={{ margin: "20px" }}>{steps[currentStep].content}</div>
      <div style={{ padding: "20px" }}>
        {currentStep > 0 && (
          <Button style={{ marginRight: "8px" }} onClick={handlePrev}>
            Previous
          </Button>
        )}
        {currentStep < steps.length - 1 && (
          <Button type="primary" onClick={handleNext}>
            Next
          </Button>
        )}
        {currentStep === steps.length - 1 && (
          <Button
            type="primary"
            disabled={!isFormDataValid(formData)}
            onClick={handleDone}
            loading={isLoading}
          >
            Done
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default AgentAdditionMoal;
